import { useState } from "react";
import { ModalLayout } from "./ModalLayout";
import {
  promptLibrary,
  PromptLibraryItem,
} from "../../data/promptLibrary";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { colors, typography, effects, components } from "../../design/tokens";
import { MarkdownContent } from "./MarkdownContent";

interface PromptLibraryModalProps {
  onClose: () => void;
  onSelectPrompt: (promptText: string) => void;
}

export function PromptLibraryModal({
  onClose,
  onSelectPrompt,
}: PromptLibraryModalProps) {
  const isCompactView = useIsCompactView();
  const [hoveredPrompt, setHoveredPrompt] = useState<PromptLibraryItem | null>(
    null
  );
  const [selectedPrompt, setSelectedPrompt] =
    useState<PromptLibraryItem | null>(null);
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handlePromptClick = (prompt: PromptLibraryItem) => {
    if (isCompactView) {
      // In compact view, don't auto-close, let user click "Use This Prompt" button
      return;
    }
    setSelectedPrompt(prompt);
    onSelectPrompt(prompt.promptText);
    onClose();
  };

  const handleExpandToggle = (promptId: string) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setExpandedPromptId(expandedPromptId === promptId ? null : promptId);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 150);
  };

  const handleUsePrompt = (prompt: PromptLibraryItem) => {
    onSelectPrompt(prompt.promptText);
    onClose();
  };

  const displayedPrompt = hoveredPrompt || selectedPrompt;

  // Compact Layout for Mobile - Custom full-screen layout (no ModalLayout to avoid duplicate X button)
  if (isCompactView) {
    return (
      <div
        className={`fixed inset-0 z-50 flex flex-col
          ${components.modal.overlay}
          transition-opacity duration-300`}
      >
        <div
          className="w-full h-full flex flex-col overflow-hidden"
          style={{ backgroundColor: colors.hex.surfaceSecondary }}
        >
          {/* Header with Back Button */}
          <div className="flex items-center px-4 py-4 border-b border-dark-border relative">
            <button
              onClick={onClose}
              className={`${components.modal.closeButton} mr-4`}
              aria-label="Close"
            >
              <LuChevronLeft size={24} />
            </button>
            <h2 className={`${typography.size["2xl"]} ${typography.weight.bold} ${colors.text.primary}`}>
              Prompt Library
            </h2>
          </div>

          {/* Scrollable Expandable Cards */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div
              className={`transition-opacity duration-300 ease-in-out ${
                isTransitioning ? "opacity-0" : "opacity-100"
              }`}
            >
              {promptLibrary.map((prompt) => {
                const isExpanded = expandedPromptId === prompt.id;
                return (
                  <div
                    key={prompt.id}
                    className={`mb-3 ${effects.borderRadius.lg} border border-dark-border
                      ${colors.surface.primary} overflow-hidden`}
                  >
                    {/* Card Header - Always Visible */}
                    <button
                      onClick={() => handleExpandToggle(prompt.id)}
                      className={`w-full text-left p-4 min-h-[44px] flex items-center justify-between
                        ${colors.text.primary}`}
                    >
                      <span className={typography.weight.medium}>{prompt.title}</span>
                      <LuChevronRight
                        size={18}
                        className={`transform ${effects.transition.transform} ${colors.text.secondary} ${
                          isExpanded ? "rotate-90" : ""
                        }`}
                      />
                    </button>

                    {/* Expanded Content - Description and Button */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-dark-border">
                        <div className="prose prose-invert max-w-none mt-4 mb-4">
                          <MarkdownContent content={prompt.description} />
                        </div>
                        <button
                          onClick={() => handleUsePrompt(prompt)}
                          className={components.button.primaryLarge + " w-full"}
                        >
                          Use This Prompt
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout (Original Two-Column)
  return (
    <ModalLayout onClose={onClose} className="!w-[900px] max-w-[90vw]">
      <div className="flex h-[600px] w-full">
        {/* Left Panel - Prompt List */}
        <div
          className={`w-[40%] border-r border-dark-border
          overflow-y-auto`}
        >
          <div className={`p-6 border-b border-dark-border`}>
            <h2 className={`${typography.size["2xl"]} ${typography.weight.bold} ${colors.text.primary}`}>
              Prompt Library
            </h2>
          </div>
          <div className="p-2">
            {promptLibrary.map((prompt) => (
              <button
                key={prompt.id}
                id={`prompt-library-item-${prompt.id}`}
                onClick={() => handlePromptClick(prompt)}
                onMouseEnter={() => setHoveredPrompt(prompt)}
                onMouseLeave={() => setHoveredPrompt(null)}
                className={`w-full text-left p-4 ${effects.borderRadius.lg} mb-2 ${effects.transition.default}
                  ${
                    hoveredPrompt?.id === prompt.id || selectedPrompt?.id === prompt.id
                      ? "bg-dark-surface-secondary text-dark-text"
                      : "text-dark-text hover:bg-dark-surface-secondary"
                  }`}
              >
                <div>{prompt.title}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Right Panel - Description */}
        <div className="w-[60%] overflow-y-auto">
          {displayedPrompt ? (
            <div className="p-6">
              <h3 className={`mb-4 ${colors.text.primary}`}>
                {displayedPrompt.title}
              </h3>
              <div className="prose prose-invert max-w-none">
                <MarkdownContent content={displayedPrompt.description} />
              </div>
            </div>
          ) : (
            <div className="p-6 flex items-center justify-center h-full">
              <p className={`${colors.text.secondary} text-center`}>
                Hover over a prompt to see its description
              </p>
            </div>
          )}
        </div>
      </div>
    </ModalLayout>
  );
}
